import { useEffect, useState, useMemo } from "react"
import { useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import { useFetchStoreImport, useFetchStoreImportStatus, useUpdateStoreImport } from "../hooks/StoreImportHooks";
import { ImportDTO, ImportCapabilityDTO, ImportStationDTO, ImportPersonDTO } from "../types/storeImportDTOs";
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';


const UserImport = () => {
    const { t } = useTranslation();
    const nav = useNavigate();
    const { organisationId } = useParams<{ organisationId: string }>();
    if (!organisationId) throw new Error("Missing organisationId from params");
    const { data, isPending, isError } = useFetchStoreImport(organisationId);
    const { data: importStatus, refetch: refetchImportStatus } = useFetchStoreImportStatus(organisationId);
    const updateStoreImportMutation = useUpdateStoreImport(organisationId);
    const [importState, setimportState] = useState<ImportDTO>();
    const [showAllCapabilities, setShowAllCapabilities] = useState(false);
    const [showAllStations, setShowAllStations] = useState(false);
    const [showAllUsers, setShowAllUsers] = useState(false);
    const [hasObservedRunningImport, setHasObservedRunningImport] = useState(false);
    const [isImportStarted, setIsImportStarted] = useState(false);

//  useEffect(() => {
//        if (isError) { nav('/login') }
//
//    }, [isError]);

    useEffect(() => {
        if (data) {
            setimportState({
                ...data,
                importUserFromStoreStations: []
            });

        }
    }, [data]);



    const filteredCapabilities = useMemo(() => {
        if (!importState) return [];
        if (showAllCapabilities) return importState.importCapabilities;
        return importState?.importCapabilities.filter(c => c.optiviCapabilityId == null);
    }, [importState, showAllCapabilities]);

    const filteredStations = useMemo(() => {
        if (!importState) return [];
        if (showAllStations) return importState.importStations;
        return importState?.importStations.filter(s => s.optiviStationId == null);
    }, [importState, showAllStations]);

    const filteredUsers = useMemo(() => {
        if (!importState) return [];
        if (showAllUsers) return importState.importPersons;
        const selectedStoreStationIds = new Set(importState.importUserFromStoreStations ?? []);

        return importState.importPersons.filter(u => {
            const isIgnoredByStation = selectedStoreStationIds.size === 0
                || !u.storePerson.stations.some(s => selectedStoreStationIds.has(s.id));

            return !isIgnoredByStation;
        });
    }, [importState, showAllUsers]);

    const selectedStoreStationIds = useMemo(() => new Set(importState?.importUserFromStoreStations ?? []), [importState?.importUserFromStoreStations]);

    const isUserIgnored = (user: ImportPersonDTO) => {
        if (!importState) return false;
        return selectedStoreStationIds.size === 0 || !user.storePerson.stations.some(s => selectedStoreStationIds.has(s.id));
    };

    const isBackgroundImportRunning = importStatus?.isRunning ?? false;
    const processedUsers = importStatus?.processedUsers ?? 0;
    const totalUsers = importStatus?.totalUsers ?? importState?.importPersons.length ?? 0;

    useEffect(() => {
        if (importStatus?.isRunning) {
            setHasObservedRunningImport(true);
            return;
        }

        if (!hasObservedRunningImport || !importStatus?.isCompleted || importStatus.isRunning) return;

        if (importStatus.errorMessage) {
            toast.error(importStatus.errorMessage);
            setHasObservedRunningImport(false);
            setIsImportStarted(false);
            return;
        }

        setIsImportStarted(false);
        toast.success(t('storeImport.importSuccess'));
        nav(-1);
    }, [hasObservedRunningImport, importStatus, nav, t]);



    if (isPending) return <h2>Hold on</h2>;
    if (isError) return <h2>{t('storeImport.notPermitted')}</h2>;

    const handleCapabilitySelect = (storeCompetencyId: number, selectedOptiviCapabilityId: string | null) => {
        if (!importState) return;

        const updatedCapabilities = importState.importCapabilities.map(c => {
            if (c.storeCompetency.id === storeCompetencyId) {
                return {
                    ...c,
                    optiviCapabilityId: selectedOptiviCapabilityId
                }
            }
            return c;
        });
        setimportState({
            ...importState,
            importCapabilities: updatedCapabilities
        });

    };

    const handleImportUserFromStationToggle = (storeStationId: number, isSelected: boolean) => {
        if (!importState) return;

        const selectedStationIds = new Set(importState.importUserFromStoreStations);
        if (isSelected) {
            selectedStationIds.add(storeStationId);
        } else {
            selectedStationIds.delete(storeStationId);
        }

        setimportState({
            ...importState,
            importUserFromStoreStations: Array.from(selectedStationIds)
        });
    };

    const handleSelectAllImportUserStations = () => {
        if (!importState) return;

        setimportState({
            ...importState,
            importUserFromStoreStations: importState.importStations.map(s => s.storeStation.id)
        });
    };

    const handleStationSelect = (storeStationId: number, selectedOptiviStationId: string | null) => {
        if (!importState) return;

        const updatedStations = importState.importStations.map(s => {
            if (s.storeStation.id === storeStationId) {

                return {
                    ...s,
                    optiviStationId: selectedOptiviStationId
                }
            }
            return s;
        });
        setimportState({
            ...importState,
            importStations: updatedStations
        });

    };

    const onSubmit: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
        e.preventDefault();

        if (!importState) return;        

        updateStoreImportMutation.mutate({ ...importState, optiviCapabilities: [], optiviStations: [], optiviUsers: [] } , {
            onSuccess: () => {
                setIsImportStarted(true);
                toast.info(t('storeImport.importWait'));
                refetchImportStatus();
            },
            onError: (error) => {
                if (error.response?.status === 409) {
                    setIsImportStarted(true);
                    toast.error('Store import is already running.');
                    refetchImportStatus();
                    return;
                }
                setIsImportStarted(false);
                toast.error(t('storeImport.importError'));
            }
        });
    };

    const onCancel: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
        e.preventDefault();
        nav(-1);
    };

 

    return (
        <div>
            {(updateStoreImportMutation.isPending || isBackgroundImportRunning || isImportStarted) &&
                <div className="spinner-container" style={{ textAlign: 'center', marginTop: '20px' }}>
                    <ClipLoader size={50} color={"#123abc"} loading={true} />
                    <p>{t('storeImport.importWait')}</p>
                    <p>{`${processedUsers} / ${totalUsers}`}</p>
                </div>
            }

            {!(updateStoreImportMutation.isPending || isBackgroundImportRunning || isImportStarted) &&

                <>
                    <div className="row mb-2 mt-2">
                        <h5 className="subtitle text-left">
                            {t('storeImport.title')}
                        </h5>
                    </div>
                    <div className="mb-3">
                        <button
                            className="btn btn-primary mt-3 me-2 mb-2"
                            onClick={onSubmit}>
                            {t('buttons.import')}
                        </button>
                        <button
                            className="btn btn-danger mt-3 me-2 mb-2"
                            onClick={onCancel}>
                            {t('buttons.cancel')}
                        </button>
                    </div>

                    <input
                        id="showAllCapabilitiesSwitch"
                        className="form-check-input"
                        type="checkbox"
                        checked={showAllCapabilities}
                        onChange={(e) => setShowAllCapabilities(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showAllCapabilitiesSwitch">
                    {`${t('storeImport.showAllCapabilities')} (${filteredCapabilities.length} / ${importState?.importCapabilities.length})`}
                    </label>


                    <table
                        className="table table-hover table-striped"
                        style={
                            {
                                "--bs-table-hover-bg": "#C9DDED"
                            } as React.CSSProperties
                        }
                    >
                        <thead>
                            <tr>
                            <th>{t('storeImport.storeCapability')}
                                </th>
                            <th>{t('storeImport.optiviCapability')}
                                </th>

                            </tr>
                        </thead>
                        <tbody>
                            {filteredCapabilities && filteredCapabilities.map((capability: ImportCapabilityDTO) => {
                             
                                return (
                                    <tr
                                        key={"capability" + capability.storeCompetency.id}>
                                        <td>{capability.storeCompetency.name}</td>
                                        <td>
                                            <select
                                                className="form-select"
                                                value={capability.optiviCapabilityId ?? ""}
                                                onChange={(e) =>
                                                    handleCapabilitySelect(
                                                        capability.storeCompetency.id,
                                                        e.target.value === "" ? null : e.target.value
                                                    )
                                                }
                                            >
                                                <option value="">Uusi</option>
                                                {importState?.optiviCapabilities.map((availableCapability) => (
                                                    <option key={availableCapability.id} value={availableCapability.id}>
                                                        {availableCapability.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <input
                        id="showAllStationsSwitch"
                        className="form-check-input"
                        type="checkbox"
                        checked={showAllStations}
                        onChange={(e) => setShowAllStations(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showAllStationsSwitch">
                    {`${t('storeImport.showAllStations')} (${filteredStations.length} / ${importState?.importStations.length})`}
                    </label>


                    <table
                        className="table table-hover table-striped"
                        style={
                            {
                                "--bs-table-hover-bg": "#C9DDED"
                            } as React.CSSProperties
                        }
                    >
                        <thead>
                            <tr>
                            <th>{t('storeImport.storeStation')}
                                </th>
                            <th>{t('storeImport.optiviStation')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStations && filteredStations.map((station: ImportStationDTO) => {
                             
                                return (
                                    <tr
                                        key={"station" + station.storeStation.id}>
                                        <td>{station.storeStation.name}</td>
                                        <td>
                                            <select
                                                className="form-select"
                                                value={station.optiviStationId ?? ""}
                                                onChange={(e) =>
                                                    handleStationSelect(
                                                        station.storeStation.id,
                                                        e.target.value === "" ? null : e.target.value
                                                    )
                                                }
                                            >
                                                <option value="">Uusi</option>
                                                {importState?.optiviStations.map((availableStation) => (
                                                    <option key={availableStation.id} value={availableStation.id}>
                                                        {availableStation.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>


               

                <div className="mt-3 mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="fw-semibold">{t('storeImport.importUsersFromStations')}</div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleSelectAllImportUserStations}
                        >
                            {t('common.fromAllStations')}
                        </button>
                    </div>
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-1">
                        {importState?.importStations.map((station) => (
                            <div className="col" key={`import-user-station-${station.storeStation.id}`}>
                                <div className="form-check">
                                    <input
                                        id={`import-user-station-${station.storeStation.id}`}
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={importState.importUserFromStoreStations.includes(station.storeStation.id)}
                                        onChange={(e) => handleImportUserFromStationToggle(station.storeStation.id, e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor={`import-user-station-${station.storeStation.id}`}>
                                        {station.storeStation.name}
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <input
                    id="showAllUsersSwitch"
                    className="form-check-input"
                    type="checkbox"
                    checked={showAllUsers}
                    onChange={(e) => setShowAllUsers(e.target.checked)}
                />

                    <label className="form-check-label" htmlFor="showAllUsersSwitch">
                    {`${t('storeImport.showAllUsers')} (${filteredUsers.length} / ${importState?.importPersons.length})`}
                    </label>

            


                    <table
                        className="table table-hover table-striped"
                        style={
                            {
                                "--bs-table-hover-bg": "#C9DDED"
                            } as React.CSSProperties
                        }
                    >
                        <thead>
                            <tr>
                            <th>{t('storeImport.storeUser')}
                                </th>
                            <th>{t('storeImport.optiviUser')}
                                </th>

                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers && filteredUsers.map((user: ImportPersonDTO) => {

                                const optiviuser = importState?.optiviUsers.find(c => c.id === user.optiviUserId) ?? null;
                                const isIgnored = isUserIgnored(user);

                                return (
                                    <tr
                                        key={"user" + user.storePerson.id}>
                                        <td>{`(${user.storePerson.email}) ${user.storePerson.firstName} ${user.storePerson.lastName}`}</td>
                                        <td>{isIgnored ? t('storeImport.ignore') : (optiviuser ? t('storeImport.update') : t('storeImport.new'))}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>}
        </div>
    );
};

export default UserImport;